/**
 * Report Studio's "Add columns": a search bar over every column the report can
 * carry, in place of a wall of chips grouped under five headings.
 *
 * Type to narrow (the label, the group and the column's id all match, every word
 * of the query must), click a result — or arrow to it and press Enter — to add
 * it; the list keeps its place so the next column is one more click. "Add all"
 * takes every match at once. A column already in the report is not offered
 * again: it is in the order list above, where it is reordered or removed.
 *
 * Two shapes. The default `panel` is the full picker — group buttons and a list
 * that is always on screen. `compact` is only the search box: the matches float
 * over whatever is beneath it while it is being used, so the control takes one
 * line of the Studio's design deck and no more.
 *
 * The options are whatever the caller passes. Today that is the Pipeline's own
 * columns (`COLUMN_DEFS`), every one of which the pipeline report may already
 * export; a catalogue-wide column choice is a separate decision (it widens what
 * a self-service export can carry) and is not made here.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Plus, Search, X } from 'lucide-react'
import type { ColumnDef } from './columnTypes'
import { matchColumns } from './studioColumns'
import { useDismiss } from './studio/controlStyles'
import type { ColumnGroup, ColumnId } from './types'

type Props = {
  options: readonly ColumnDef[]
  selected: readonly ColumnId[]
  /** Group names in the order the Studio has always shown them. */
  groups: readonly ColumnGroup[]
  onAdd: (ids: ColumnId[]) => void
  variant?: 'panel' | 'compact'
}

export function ColumnSearchPicker({ options, selected, groups, onAdd, variant = 'panel' }: Props) {
  const compact = variant === 'compact'
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<ColumnGroup | 'All'>('All')
  const [active, setActive] = useState(0)
  // Compact only: whether the floating matches are showing.
  const [open, setOpen] = useState(false)
  const listRef = useRef<HTMLUListElement | null>(null)
  const rootRef = useDismiss<HTMLDivElement>(compact && open, () => setOpen(false))

  const matches = useMemo(
    () => matchColumns(options, selected, query, group),
    [options, selected, query, group],
  )
  const available = options.length - selected.filter((id) => options.some((o) => o.id === id)).length

  // Adding a column removes it from the list; keep the highlight on the row now
  // in its place rather than letting it run off the end.
  useEffect(() => {
    setActive((current) => Math.min(current, Math.max(matches.length - 1, 0)))
  }, [matches.length])

  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>('[data-active="true"]')
    row?.scrollIntoView?.({ block: 'nearest' })
  }, [active, matches])

  // The full picker lives in a short pane, and a narrowed list is shorter still:
  // bring the search and its matches to the top of the pane so they are not left
  // below the fold. The compact picker floats, so it never needs this.
  const revealResults = () => {
    if (!compact) rootRef.current?.scrollIntoView?.({ block: 'start' })
  }
  useEffect(() => {
    if (query) revealResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reveal on a new query only
  }, [query])

  const add = (ids: ColumnId[]) => {
    if (ids.length) onAdd(ids)
  }

  const listVisible = !compact || open

  const input = (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-stone-500" />
      <input
        type="text"
        role="combobox"
        aria-label="Search columns"
        aria-expanded={listVisible}
        aria-controls="column-search-results"
        aria-activedescendant={matches[active] && listVisible ? `column-option-${matches[active].id}` : undefined}
        value={query}
        onFocus={() => {
          revealResults()
          if (compact) setOpen(true)
        }}
        placeholder={
          compact
            ? `Search ${available} columns to add…`
            : `Search ${available} columns — a name, a group, a figure…`
        }
        onChange={(event) => {
          setQuery(event.target.value)
          setActive(0)
          if (compact) setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            if (compact) setOpen(true)
            setActive((current) => Math.min(current + 1, matches.length - 1))
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((current) => Math.max(current - 1, 0))
          } else if (event.key === 'Enter') {
            event.preventDefault()
            const column = matches[active]
            if (column) add([column.id])
          } else if (event.key === 'Escape' && (query || open)) {
            event.preventDefault()
            setQuery('')
            setOpen(false)
          }
        }}
        className="w-full rounded border border-white/[0.08] bg-black/40 py-1.5 pl-7 pr-7 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/40 focus:outline-none"
      />
      {query ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => setQuery('')}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-500 hover:text-stone-200"
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  )

  const optionList = (
    <ul
      id="column-search-results"
      ref={listRef}
      role="listbox"
      aria-label="Columns you can add"
      className={clsx(
        'overflow-y-auto',
        compact
          ? 'max-h-60'
          : 'mt-1.5 max-h-56 min-h-[9rem] rounded border border-white/[0.05] bg-black/20',
      )}
    >
      {matches.length === 0 ? (
        <li className="px-2 py-3 text-center text-[10px] text-stone-600">
          {available === 0
            ? 'Every column is already in the report.'
            : 'No column matches. Try fewer words, or clear the group.'}
        </li>
      ) : (
        matches.map((column, index) => (
          <li
            key={column.id}
            id={`column-option-${column.id}`}
            role="option"
            aria-selected={index === active}
            data-active={index === active}
            onMouseEnter={() => setActive(index)}
            onClick={() => add([column.id])}
            className={clsx(
              'flex cursor-pointer items-center gap-2 px-2 py-1 text-[11px]',
              index === active ? 'bg-amber-300/[0.08] text-amber-100' : 'text-stone-300 hover:bg-white/[0.03]',
            )}
          >
            <Plus className="h-2.5 w-2.5 shrink-0 text-stone-500" />
            <span className="min-w-0 flex-1 truncate">{column.label}</span>
            <span className="shrink-0 text-[9px] uppercase tracking-[0.12em] text-stone-600">
              {column.group}
            </span>
          </li>
        ))
      )}
    </ul>
  )

  const addAll = (
    <button
      type="button"
      disabled={matches.length === 0}
      onClick={() => add(matches.map((column) => column.id))}
      className="rounded border border-amber-300/25 px-1.5 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/[0.08] disabled:opacity-30"
    >
      Add all {matches.length > 0 ? matches.length : ''}
    </button>
  )

  if (compact) {
    return (
      <div data-testid="column-search-picker" ref={rootRef} className="relative">
        {input}
        {open ? (
          <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-lg border border-amber-300/20 bg-[#121212] shadow-2xl">
            {optionList}
            <div className="flex items-center gap-2 border-t border-white/[0.06] px-2 py-1.5">
              <span className="text-[9px] text-stone-500" aria-live="polite">
                {matches.length === 0
                  ? 'No match'
                  : `${matches.length} column${matches.length === 1 ? '' : 's'}`}
              </span>
              <span className="flex-1" />
              {addAll}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div data-testid="column-search-picker" ref={rootRef}>
      {input}

      <div className="mt-1.5 flex flex-wrap items-center gap-1" role="group" aria-label="Narrow by group">
        {(['All', ...groups] as const).map((name) => (
          <button
            key={name}
            type="button"
            aria-pressed={group === name}
            onClick={() => {
              setGroup(name)
              setActive(0)
            }}
            className={clsx(
              'rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em]',
              group === name
                ? 'border-amber-300/45 bg-amber-300/[0.1] text-amber-200'
                : 'border-white/[0.06] text-stone-500 hover:text-stone-300',
            )}
          >
            {name}
          </button>
        ))}
        <span className="flex-1" />
        <span className="text-[9px] text-stone-600" aria-live="polite">
          {matches.length === 0
            ? 'No match'
            : `${matches.length} column${matches.length === 1 ? '' : 's'}`}
        </span>
        {addAll}
      </div>

      {optionList}
    </div>
  )
}
